import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Users, ClipboardList, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const HeaderContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-light);
  padding: 24px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const GreetingSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderLogo = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  border-radius: var(--border-radius-sm);
  opacity: 0.8;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
`;

const TextSection = styled.div`
  flex: 1;
`;

const Greeting = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
`;

const InfoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
  }
`;

const DateTimeDisplay = styled.div`
  text-align: left;
  
  @media (max-width: 768px) {
    text-align: right;
  }
`;

const DateText = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 4px;
`;

const TimeText = styled.div`
  font-size: 20px;
  font-weight: 500;
  color: var(--text-primary);
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary-light);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
`;

const UserDetails = styled.div`
  text-align: left;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;

const UserRole = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SupervisorBadge = styled.span`
  background: var(--warning-light);
  color: var(--warning-color);
  padding: 2px 6px;
  border-radius: var(--border-radius-sm);
  font-size: 10px;
  font-weight: 500;
`;

const QuickActions = styled.div`
  display: flex;
  gap: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const QuickActionButton = styled.button`
  background: transparent;
  color: var(--primary-color);
  border: 1px solid var(--primary-color)40;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #0073ea10;
    border-color: var(--primary-color);
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`;

const LogoutButton = styled.button`
  background: none;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  padding: 8px 16px;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    border-color: var(--error-color);
    color: var(--error-color);
    background: var(--error-light);
  }
`;

const DashboardHeader: React.FC = () => {
  const { user, logout } = useAuth();
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = currentDateTime.getHours();
    if (hour < 12) return 'בוקר טוב';
    if (hour < 17) return 'צהריים טובים';
    if (hour < 21) return 'ערב טוב';
    return 'לילה טוב';
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return parts[0][0] + parts[1][0];
    }
    return name.substring(0, 2);
  };

  const formatDate = () => {
    return currentDateTime.toLocaleDateString('he-IL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = () => {
    return currentDateTime.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  if (!user) return null;

  return (
    <HeaderContainer>
      <GreetingSection>
        <LogoContainer>
          <HeaderLogo
            src="/logomagialecha.png"
            alt="לוגו מגיע לך"
            loading="lazy"
          />
        </LogoContainer>
        <TextSection>
          <Greeting>{getGreeting()}, {user.fullName}</Greeting>
          <Subtitle>ברוך הבא למערכת ניהול זכויות ביטוח לאומי</Subtitle>
        </TextSection>
      </GreetingSection>
      
      <InfoSection>
        <DateTimeDisplay>
          <DateText>{formatDate()}</DateText>
          <TimeText>{formatTime()}</TimeText>
        </DateTimeDisplay>
        
        <UserInfo>
          <UserAvatar>
            {getInitials(user.fullName)}
          </UserAvatar>
          <UserDetails>
            <UserName>{user.fullName}</UserName>
            <UserRole>
              {user.permissionLevelName}
              {user.isSupervisor && (
                <SupervisorBadge>מפקח</SupervisorBadge>
              )}
            </UserRole>
          </UserDetails>
        </UserInfo>
        
        <QuickActions>
          {(user.permissionLevel === 1 || user.permissionLevel === 3) && ( // System Admin or Case Manager
            <QuickActionButton onClick={() => window.location.href = '/clients'}>
              <Users size={14} />
              ניהול לקוחות
            </QuickActionButton>
          )}
          {(user.permissionLevel === 1 || user.permissionLevel === 2) && ( // System Admin or Sales Rep
            <QuickActionButton onClick={() => window.location.href = '/leads'}>
              <ClipboardList size={14} />
              ניהול לידים
            </QuickActionButton>
          )}
        </QuickActions>

        <LogoutButton onClick={handleLogout}>
          <LogOut size={14} />
          יציאה
        </LogoutButton>
      </InfoSection>
    </HeaderContainer>
  );
};

export default DashboardHeader;